import { useBoxBuilder } from "@/lib/store";
import { formatUsd } from "@/lib/pricing";
import type { PremiumItem } from "@/lib/types";
import { cn } from "@/lib/cn";

export default function PremiumAddOns({ items }: { items: PremiumItem[] }) {
  const selectedPremium = useBoxBuilder((s) => s.selectedPremium);
  const togglePremium = useBoxBuilder((s) => s.togglePremium);

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((item) => {
        const selected = selectedPremium.includes(item.id);
        const outOfStock = item.stock <= 0;
        return (
          <label
            key={item.id}
            className={cn(
              "card flex cursor-pointer items-stretch gap-4 p-4 transition hover:-translate-y-0.5 hover:shadow-pop",
              selected && "bg-brand-50 ring-2 ring-brand-500",
              outOfStock && "cursor-not-allowed opacity-60 hover:translate-y-0",
            )}
          >
            <div className="relative h-24 w-24 shrink-0 self-center overflow-hidden rounded-xl bg-ink-50">
              {item.image ? (
                <img
                  src={item.image}
                  alt=""
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-2xl font-display text-ink-200">
                  {item.title.charAt(0)}
                </div>
              )}
            </div>
            <div className="flex min-w-0 flex-1 flex-col">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-base font-semibold text-ink-800 line-clamp-2 sm:text-lg">
                    {item.title}
                  </div>
                  <div className="mt-1 text-sm font-semibold uppercase tracking-[0.12em] text-brand-600">
                    +{formatUsd(item.surchargeCents)}
                  </div>
                </div>
              </div>
              {item.description ? (
                <p className="mt-2 text-sm text-ink-500 line-clamp-2">
                  {item.description}
                </p>
              ) : null}
              <div className="mt-auto pt-3">
                <span
                  className={cn(
                    "inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-semibold transition",
                    selected
                      ? "bg-brand-500 text-white"
                      : "bg-white text-ink-600 ring-1 ring-ink-200",
                  )}
                >
                  <span
                    className={cn(
                      "flex h-4 w-4 items-center justify-center rounded-full",
                      selected
                        ? "bg-white text-brand-500"
                        : "border border-ink-300 bg-white text-ink-300",
                    )}
                    aria-hidden
                  >
                    {selected ? "\u2713" : "+"}
                  </span>
                  {selected ? "Added" : "Add"}
                </span>
              </div>
            </div>
            <input
              type="checkbox"
              className="sr-only"
              checked={selected}
              disabled={outOfStock}
              onChange={() => !outOfStock && togglePremium(item.id)}
              aria-label={`Add ${item.title} for ${formatUsd(item.surchargeCents)}`}
            />
          </label>
        );
      })}
    </div>
  );
}
